import { useState } from 'react';
import { useEffect, useRef } from 'react';
import { useEventListener } from 'hooks/useEventListener';
import scroll from 'utilities/scroll';

export const useHandlers = ({
    autofocus,
    focusActions,
    focusAnimated,
    isModelPropUndefined,
    modelProp,
    modelPropOrInvalidProp,
    onBlur,
    searchable,
    setTrigger,
    userProfile,
    validateForm,
}) => {
    const [autoBlur, setAutoBlur] = useState(false);
    const [classFocus, setClassFocus] = useState('');
    const [offset, setOffset] = useState(0);
    const [parentElement, setParentElement] = useState('');

    const currentElement = useRef(null);

    const isModelPropOrInvalidPropUndefined = typeof userProfile[modelPropOrInvalidProp] === 'undefined';

    const changeClassFocus = change => {
        const classFocus = change ? 'form__select--focus' : '';
        setClassFocus(classFocus);
    };
    const calculateOffset = element => {
        const bodyRect = document.body.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const innerHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;

        return elementRect.top - bodyRect.top - innerHeight / 2;
    };
    const focusNextElement = () => {
        const nextElement = currentElement.current.parentElement.parentElement.parentElement.nextSibling;

        if (!nextElement) return;

        setTimeout(() => {
            // setTimeout is necessary because BlockWrapper delays loading
            const element = nextElement.querySelectorAll('input, select');
            const elementHasLenght = element.length > 0;
            elementHasLenght && element[0].focus();
        }, 100);
    };

    const onFocusAnimate = event => {
        if (event) {
            const parentElement = document.getElementById(`id_wrapper_${modelProp}`);
            scroll.transition(parentElement);
            // set offset to state
            const offset = calculateOffset(parentElement);
            setOffset(offset);
            setParentElement(parentElement);
        } else if (offset) {
            scroll.transition(parentElement);
            scroll.offset(offset);
        }
    };

    const handleOnFocus = event => {
        focusAnimated && onFocusAnimate(event);

        !isModelPropUndefined && focusActions.selectFalseFocus(modelProp);

        searchable && changeClassFocus(true);
    };

    const handleOnBlur = () => {
        const value = !isModelPropOrInvalidPropUndefined ? userProfile[modelPropOrInvalidProp] : '';

        if (onBlur) {
            onBlur(value);

            return;
        }

        validateForm(value);
        setTrigger(true);
        changeClassFocus(false);
    };

    const handleOnChange = ({
        event,
        formActions,
        changeClassError,
        onChange,
        validations,
        profileModel,
        profileModelLabel,
    }) => {
        const value = event?.value || '';
        const label = event?.label || '';

        formActions.change(profileModel, value);
        formActions.change(profileModelLabel, label);

        validations && formActions.validate(profileModel, validations);

        const animate = value !== '';
        animate && formActions.validate(profileModel, { required: true });

        !searchable && changeClassFocus(animate);

        onChange?.(value);

        changeClassError(false);
        setTrigger(true);

        focusNextElement();
    };

    useEventListener('resize', () => {
        const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        setAutoBlur(width < 768);
    });

    useEffect(() => {
        autofocus && currentElement.current.focus();
    }, [autofocus, userProfile]);

    return {
        autoBlur,
        classFocus,
        currentElement,
        handleOnBlur,
        handleOnChange,
        handleOnFocus,
    };
};
